import React from 'react';

import { EFieldName } from '../../constants';
import { Form } from '../../components/Form';
import { InputWithOptions } from '../../components/InputWithOptions';
import { InputNumberField } from '../../components/InputNumberField';
import { calculatePercentageOfObjectCost } from '../../utils';

import {
  LOAN_TERM_OPTIONS,
  MIN_OBJECT_COST,
  MAX_OBJECT_COST,
  MIN_LOAN_TERM,
  MAX_LOAN_TERM,
  MIN_DOWN_PAYMENT_PERCENTAGE,
  MAX_DOWN_PAYMENT_PERCENTAGE,
} from './constants';
import { useFormCalculator } from './hooks/useFormCalculator';
import { IFetchOffersProps } from '../../hooks/useGetOffers';
import { ETypeChangeInput } from '../../utils/tracking/types';
import { InsuranceField } from 'shared/mortgageMiniCalculator/components/InsuranceField/InsuranceField';
import { ProgramsField } from 'shared/mortgageMiniCalculator/components/ProgramsField';
import { useApplicationContext } from 'shared/common/utils/applicationContext';
import { ERuntimeSettings } from 'shared/common/types/runtimeSettings';
import { useIsDesktop } from '../../hooks/useIsDesktop';

interface IFormContainerProps {
  fetchOffers: (params: IFetchOffersProps) => void;
}

export const FormContainer: React.FC<IFormContainerProps> = ({ fetchOffers }) => {
  const {
    objectCost,
    downPayment,
    loanTermYears,
    downPaymentOptions,
    getHandlerByFieldName,
    lifeInsurance,
    benefitProgram,
  } = useFormCalculator(fetchOffers);
  const isDesktop = useIsDesktop();
  const { config } = useApplicationContext();
  const isLifeInsuranceFieldEnabled = config.get<boolean>(ERuntimeSettings.lifeInsuranceFieldEnabled) ?? false;
  const isProgramFilterFieldEnabled = config.get<boolean>(ERuntimeSettings.programFilterFieldEnabled) ?? false;

  return (
    <Form>
      <InputNumberField
        label={'Стоимость недвижимости'}
        value={objectCost}
        name={EFieldName.ObjectCost}
        onChange={getHandlerByFieldName(EFieldName.ObjectCost, ETypeChangeInput.MANUAL)}
        rightAdornment={'₽'}
        min={MIN_OBJECT_COST}
        max={MAX_OBJECT_COST}
      />
      {isDesktop && isProgramFilterFieldEnabled && (
        <ProgramsField
          value={benefitProgram}
          onChange={getHandlerByFieldName(EFieldName.benefitProgram, ETypeChangeInput.AUTO)}
        />
      )}
      <InputWithOptions
        label={'Первый взнос'}
        value={downPayment}
        name={EFieldName.DownPayment}
        options={downPaymentOptions}
        chipValue={downPayment}
        onChange={getHandlerByFieldName(EFieldName.DownPayment, ETypeChangeInput.MANUAL)}
        onClickChip={getHandlerByFieldName(EFieldName.DownPayment, ETypeChangeInput.AUTO)}
        rightAdornment={'₽'}
        min={calculatePercentageOfObjectCost(objectCost, MIN_DOWN_PAYMENT_PERCENTAGE)}
        max={calculatePercentageOfObjectCost(objectCost, MAX_DOWN_PAYMENT_PERCENTAGE)}
      />
      <InputWithOptions
        label={'Срок кредита'}
        value={loanTermYears}
        name={EFieldName.loanTermYears}
        options={LOAN_TERM_OPTIONS}
        chipValue={loanTermYears}
        onChange={getHandlerByFieldName(EFieldName.loanTermYears, ETypeChangeInput.MANUAL)}
        onClickChip={getHandlerByFieldName(EFieldName.loanTermYears, ETypeChangeInput.AUTO)}
        rightAdornment={'лет'}
        min={MIN_LOAN_TERM}
        max={MAX_LOAN_TERM}
      />
      {!isDesktop && isProgramFilterFieldEnabled && (
        <ProgramsField
          value={benefitProgram}
          onChange={getHandlerByFieldName(EFieldName.benefitProgram, ETypeChangeInput.AUTO)}
        />
      )}
      {isLifeInsuranceFieldEnabled && (
        <InsuranceField
          lifeInsurance={lifeInsurance ?? false}
          onChange={(value: boolean) =>
            getHandlerByFieldName(EFieldName.lifeInsurance, ETypeChangeInput.MANUAL)(value ? 1 : 0)
          }
        />
      )}
    </Form>
  );
};

FormContainer.displayName = 'FormContainer';
